import { ArrowLeft, LucideIcon } from "lucide-react";
import { Button } from "../buttons/button";
import { cn } from "@drpcrd/utils/shared";
import { ElementType } from "react";

type ButtonConfig = {
  text?: string;
  icon?: LucideIcon;
  disabled?: boolean;
  as?: ElementType;
  href?: string;
  target?: string;
  rel?: string;
  className?: string;
  onClick?: () => void;
};

type ExtendedButtonConfig = ButtonConfig & {
  loading?: boolean;
};

type ReturnButtonConfig = {
  show?: boolean;
  onClick?: () => void;
};

type Props = {
  primary?: ExtendedButtonConfig;
  secondary?: ExtendedButtonConfig;
  return?: ReturnButtonConfig;
};

/**
 * Renders primary and secondary action buttons for creator pages
 */
export const CreatorPageButtons = ({
  primary,
  secondary,
  return: returnBtn,
}: Props) => {
  const showComponent = primary?.text || secondary?.text;

  if (!showComponent) {
    return null;
  }

  return (
    <>
      {/* Fixed buttons spacer – Bottom spacing to prevent content from being hidden behind fixed buttons */}
      <div
        className={cn(
          // 86px is the height of the button (38px) + the vertical margins (24px + 24px)
          // 140px is the height of two buttons (38px * 2) + the gap (16px) + the vertical margins (24px + 24px)
          primary && secondary ? "h-[140px]" : "h-[86px]",
          "mt-auto w-full md:mt-0",
        )}
      />

      {/* Buttons */}
      <div className="fixed bottom-0 left-0 w-full space-y-2 bg-white px-2 py-3">
        {secondary?.text && (
          <Button
            as={secondary.as}
            href={secondary.href}
            target={secondary.target}
            rel={secondary.rel}
            variant="outlined"
            icon={secondary.icon}
            className={cn(
              "w-full transition-colors duration-200",
              secondary.className,
            )}
            disabled={secondary.disabled || primary?.loading}
            loading={secondary.loading}
            onClick={secondary.onClick}
            aria-disabled={secondary.disabled}
          >
            {secondary.text}
          </Button>
        )}

        <div className={cn("flex flex-row gap-0", returnBtn?.show && "gap-2")}>
          {returnBtn?.show && (
            <Button
              variant="text"
              size="md"
              onClick={returnBtn.onClick}
              icon={ArrowLeft}
              className="text-primary transition-colors duration-200"
              disabled={primary?.loading}
            >
              Back
            </Button>
          )}

          {primary?.text && (
            <Button
              as={primary.as}
              href={primary.href}
              target={primary.target}
              rel={primary.rel}
              variant="filled"
              icon={primary.icon}
              size="md"
              className={cn(
                "w-full transition-colors duration-200",
                primary.className,
              )}
              disabled={primary.disabled || secondary?.loading}
              onClick={primary.onClick}
              aria-disabled={primary.disabled}
              loading={primary.loading}
            >
              {primary.text}
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
